import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #subscriptionUuidBackfillInFlight = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Ensure subscription line items always have a unique uuid in cart, even if they were
    // added via older flows or third-party scripts.
    queueMicrotask(() => {
      this.#ensureSubscriptionLineItemUuids();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Ensures every subscription line item has a unique `_subscription_uuid` property.
   * If an item is missing one (or duplicates another), we update its properties via cart/change.
   */
  async #ensureSubscriptionLineItemUuids() {
    if (this.#subscriptionUuidBackfillInFlight) return;
    this.#subscriptionUuidBackfillInFlight = true;

    try {
      const createSubscriptionId = () => {
        // Short + unique-enough for line item correlation (not crypto-grade).
        // Example: "m5z8k2p4q1h7x9ab"
        const timePart = Date.now().toString(36);
        const randomPart = Math.random().toString(36).slice(2, 10);
        return `${timePart}${randomPart}`;
      };

      const fetchCart = async () => {
        const response = await fetch('/cart.js', { credentials: 'same-origin' });
        return response.json();
      };

      const cart = await fetchCart();
      const items = Array.isArray(cart?.items) ? cart.items : [];

      const subscriptionItems = items.filter((item) => Boolean(item?.selling_plan_allocation));
      if (subscriptionItems.length === 0) return;

      /** @type {Set<string>} */
      const seen = new Set();
      /** @type {Array<{ key: string, quantity: number, properties: Record<string, unknown> }>} */
      const toUpdate = [];

      for (const item of subscriptionItems) {
        const key = item?.key;
        if (!key) continue;

        const currentProperties = /** @type {Record<string, unknown>} */ (item.properties || {});
        const rawUuid = currentProperties?._subscription_uuid;
        const uuid = (rawUuid == null ? '' : String(rawUuid)).trim();

        const needsUuid = !uuid;
        const isDuplicate = uuid ? seen.has(uuid) : false;

        if (uuid) seen.add(uuid);

        if (needsUuid || isDuplicate) {
          const newUuid = createSubscriptionId();
          // Ensure we don't create another duplicate in the same pass.
          seen.add(newUuid);
          toUpdate.push({
            key,
            quantity: Number(item.quantity) || 1,
            properties: {
              ...currentProperties,
              _subscription_uuid: newUuid,
              // Keep these consistent if downstream tools rely on them.
              _subscription: currentProperties?._subscription ?? 'True',
            },
          });
        }
      }

      if (toUpdate.length === 0) return;

      // Update cart items sequentially to avoid race conditions.
      // Use the same "sections" flow as quantity updates so the cart UI refreshes correctly.
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      let lastResponse;
      for (const update of toUpdate) {
        const body = JSON.stringify({
          id: update.key,
          quantity: update.quantity,
          properties: update.properties,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });

        const responseText = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body })).then((r) =>
          r.text()
        );
        lastResponse = JSON.parse(responseText);
      }

      if (!lastResponse?.sections?.[this.sectionId]) return;

      const newSectionHTML = new DOMParser().parseFromString(lastResponse.sections[this.sectionId], 'text/html');
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: lastResponse.sections,
        })
      );

      morphSection(this.sectionId, lastResponse.sections[this.sectionId]);
    } catch (error) {
      console.error(error);
    } finally {
      this.#subscriptionUuidBackfillInFlight = false;
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const bundleGroupId = cartItemRowToRemove.dataset.bundleGroupId;
    const bundleRole = cartItemRowToRemove.dataset.bundleRole;

    // If this is a bundle parent, remove the entire bundle group (parent + hidden components).
    if (bundleGroupId && bundleRole === 'parent') {
      const rowsInGroup = this.refs.cartItemRows.filter((row) => row.dataset.bundleGroupId === bundleGroupId);
      const keysToRemove = Array.from(
        new Set(rowsInGroup.map((row) => row.dataset.key).filter((key) => typeof key === 'string' && key.length > 0))
      );

      // Optimistically animate-remove all rows in the group.
      this.#animateRowsRemoval(rowsInGroup);

      await this.#removeCartItemsByKey(keysToRemove, 'clear-bundle');
      return;
    }

    // Default single-line remove.
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    this.#animateRowsRemoval(rowsToRemove);
  }

  /**
   * Removes cart items by their line-item keys (stable; avoids line shifting).
   * Only the final request asks Shopify for `sections` to refresh the UI once.
   *
   * @param {string[]} keys
   * @param {string} action
   */
  async #removeCartItemsByKey(keys, action) {
    if (!keys.length) return;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);
    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    cartTotal?.shimmer();

    try {
      let lastResponseText = null;

      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const isLast = i === keys.length - 1;

        const payload = isLast
          ? {
              id: key,
              quantity: 0,
              sections: Array.from(sectionsToUpdate).join(','),
              sections_url: window.location.pathname,
            }
          : { id: key, quantity: 0 };

        const body = JSON.stringify(payload);

        const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
        lastResponseText = await response.text();
      }

      if (!lastResponseText) return;

      const parsedResponseText = JSON.parse(lastResponseText);
      resetShimmer(this);

      if (parsedResponseText.errors) {
        // Fall back to a full section rerender if Shopify returns an error shape.
        sectionRenderer.renderSection(this.sectionId, { cache: false });
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(parsedResponseText.sections[this.sectionId], 'text/html');
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * @param {HTMLElement[]} rows
   */
  #animateRowsRemoval(rows) {
    rows.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion() || row.hasAttribute('hidden')) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      // Re-check after cart updates, in case a third-party add flow bypassed our add-to-cart hook.
      queueMicrotask(() => {
        this.#ensureSubscriptionLineItemUuids();
      });
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      queueMicrotask(() => {
        this.#ensureSubscriptionLineItemUuids();
      });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Handles the subscribe toggle checkbox.
   * Removes the one-time item and re-adds it with a selling plan.
   * @param {Event} event - The change event from the checkbox.
   */
  async onSubscribeToggle(event) {
    const checkbox = event.target;
    if (!checkbox || !checkbox.checked) return;

    const variantId = checkbox.dataset.variantId;
    const sellingPlanId = checkbox.dataset.sellingPlanId;
    const quantity = parseInt(checkbox.dataset.quantity, 10) || 1;
    const line = parseInt(checkbox.dataset.line, 10);

    if (!variantId || !sellingPlanId || !line) return;

    this.classList.add('cart-items-disabled');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    try {
      // Step 1: Remove the one-time purchase item
      await fetch(
        `${Theme.routes.cart_change_url}`,
        fetchConfig('json', {
          body: JSON.stringify({ line, quantity: 0 }),
        })
      );

      // Step 2: Re-add with selling plan
      const createSubscriptionId = () => {
        const timePart = Date.now().toString(36);
        const randomPart = Math.random().toString(36).slice(2, 10);
        return `${timePart}${randomPart}`;
      };

      const normalizeSellingPlanId = (raw) => {
        const value = (raw || '').toString().trim();
        if (!value) return '';
        if (value.includes('SellingPlan/')) {
          const lastSegment = value.split('/').pop() || '';
          return lastSegment.split('?')[0] || '';
        }
        return value;
      };

      const subscriptionUuid = createSubscriptionId();
      const numericPlanId = normalizeSellingPlanId(sellingPlanId);

      const addBody = {
        id: parseInt(variantId, 10),
        quantity: quantity,
        selling_plan: numericPlanId,
        properties: {
          _selling_plan_id: numericPlanId,
          _subscription: 'True',
          _subscription_uuid: subscriptionUuid,
        },
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      };

      const addResponse = await fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body: JSON.stringify(addBody) }));
      const addResult = JSON.parse(await addResponse.text());

      if (addResult.sections?.[this.sectionId]) {
        const newSectionHTML = new DOMParser().parseFromString(addResult.sections[this.sectionId], 'text/html');
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: addResult.sections,
          })
        );

        morphSection(this.sectionId, addResult.sections[this.sectionId]);
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
    } catch (error) {
      console.error('Failed to convert to subscription:', error);
      checkbox.checked = false;
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      this.classList.remove('cart-items-disabled');
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
